import { useRef, useState } from 'react';
import { UserRound } from 'lucide-react';
import type { InvoicePatchDTO } from '@/types';
import { useGoogleMapsAutocomplete, getAddressWithZipCode } from '@/hooks/useGoogleMapsAutocomplete';
import { ItemEditorDialog } from './ui/ItemEditorDialog';
import { PhoneInput } from './ui/PhoneInput';
import { LoadingSpinner } from './ui/LoadingSpinner';

interface BillToValues {
    client_name: string;
    client_phone: string;
    client_email: string;
    client_address: string;
    client_unit_name: string;
}

interface InvoiceBillToSheetProps {
    initial: BillToValues;
    onClose: () => void;
    /** Receives only the fields that changed. */
    onSave: (patch: InvoicePatchDTO) => Promise<void>;
}

const labelClass = 'text-xs font-semibold text-emerald-600 uppercase tracking-wide block mb-1';

/** Mobile sheet that edits all of an invoice's Bill To details at once. */
export function InvoiceBillToSheet({ initial, onClose, onSave }: InvoiceBillToSheetProps) {
    const [draft, setDraft] = useState<BillToValues>(initial);
    const [isSaving, setIsSaving] = useState(false);
    const addressRef = useRef<HTMLInputElement>(null);

    useGoogleMapsAutocomplete({
        inputRef: addressRef,
        onPlaceSelect: (place) => {
            const address = getAddressWithZipCode(place);
            if (address) setDraft(d => ({ ...d, client_address: address }));
        },
    });

    const set = (field: keyof BillToValues) => (value: string) => setDraft(d => ({ ...d, [field]: value }));

    const handleSave = async () => {
        const patch: InvoicePatchDTO = {};
        (Object.keys(draft) as (keyof BillToValues)[]).forEach((key) => {
            if (draft[key] !== initial[key]) patch[key] = draft[key] || null;
        });
        if (Object.keys(patch).length > 0) {
            setIsSaving(true);
            try {
                await onSave(patch);
            } finally {
                setIsSaving(false);
            }
        }
        onClose();
    };

    return (
        <ItemEditorDialog
            title="Bill To"
            icon={UserRound}
            onClose={onClose}
            footer={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                        {isSaving && <LoadingSpinner />}
                        Save
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                <div>
                    <label className={labelClass}>Client Name</label>
                    <input
                        type="text"
                        className="input-field text-sm"
                        placeholder="Add client name..."
                        value={draft.client_name}
                        onChange={(e) => set('client_name')(e.target.value)}
                    />
                </div>
                <div>
                    <label className={labelClass}>Phone</label>
                    <PhoneInput
                        className="input-field text-sm"
                        placeholder="Add phone..."
                        value={draft.client_phone}
                        onChange={set('client_phone')}
                    />
                </div>
                <div>
                    <label className={labelClass}>Email</label>
                    <input
                        type="email"
                        className="input-field text-sm"
                        placeholder="Add email..."
                        value={draft.client_email}
                        onChange={(e) => set('client_email')(e.target.value)}
                    />
                </div>
                <div>
                    <label className={labelClass}>Address</label>
                    <input
                        ref={addressRef}
                        type="text"
                        className="input-field text-sm"
                        placeholder="Add address..."
                        value={draft.client_address}
                        onChange={(e) => set('client_address')(e.target.value)}
                    />
                </div>
                <div>
                    <label className={labelClass}>Unit</label>
                    <input
                        type="text"
                        className="input-field text-sm"
                        placeholder="Apt, suite, unit..."
                        value={draft.client_unit_name}
                        onChange={(e) => set('client_unit_name')(e.target.value)}
                    />
                </div>
            </div>
        </ItemEditorDialog>
    );
}
